/**
 * v136.0 — Cloud sync client.
 *
 * Push/pull Range Log v3 sessions to/from the Cloudflare Worker fronting D1.
 *
 * Identity flow:
 *   - Every request carries X-Sparrow-Customer-Id, X-Sparrow-Customer-Email,
 *     and X-Sparrow-Customer-Hash headers from the identityBridge.
 *   - The Worker recomputes the HMAC and rejects mismatches with 401.
 *   - Anonymous users never call these functions (the Sign-In CTA gates them).
 *
 * Error handling:
 *   - Network failures resolve with `{ ok: false, error }` rather than throwing
 *     — the UI is expected to show a non-blocking toast and let the user retry.
 *     Sync is best-effort augmentation of local storage; it never blocks
 *     anything.
 *   - 401 errors fire a `signedOut` event the IdentityProvider can listen for
 *     so we don't keep retrying on a stale identity.
 *
 * Storage:
 *   - The Worker is the source of truth ONLY for cross-device replication.
 *     The browser's localStorage v3 store is still the canonical local copy.
 *     A cloud pull merges new sessions in; conflicts resolve to whichever
 *     `updated_at` is newer.
 */

import type { ShotSession } from '@/range/types';
import type { BowProfile, ArrowProfile } from '@/profiles/types';
import { fetchIdentity, type SparrowIdentity } from '@/auth/identityBridge';

/**
 * Cloudflare Worker URL. UPDATE AFTER WORKER DEPLOY — replace with the URL
 * returned by `wrangler deploy`.
 *
 * Until then, calls fail fast and the UI shows "Cloud sync is initializing"
 * rather than erroring out the whole Range Log.
 */
export const CLOUD_SYNC_BASE = 'https://sparrow-sync.salmisseri.workers.dev';

const HEADER_CUSTOMER_ID = 'X-Sparrow-Customer-Id';
const HEADER_CUSTOMER_EMAIL = 'X-Sparrow-Customer-Email';
const HEADER_CUSTOMER_HASH = 'X-Sparrow-Customer-Hash';

export interface CloudSyncResult<T = unknown> {
  ok: boolean;
  data?: T;
  error?: string;
}

function isReady(identity: SparrowIdentity): identity is SparrowIdentity & {
  customerId: string;
  customerHash: string;
} {
  return Boolean(identity.signedIn && identity.customerId && identity.customerHash);
}

function authHeaders(identity: SparrowIdentity): HeadersInit | null {
  if (!isReady(identity)) return null;
  return {
    [HEADER_CUSTOMER_ID]: identity.customerId,
    [HEADER_CUSTOMER_HASH]: identity.customerHash,
    ...(identity.customerEmail ? { [HEADER_CUSTOMER_EMAIL]: identity.customerEmail } : {}),
  };
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const identity = await fetchIdentity();
  const headers = authHeaders(identity);
  if (!headers) throw new Error('not_signed_in');
  return fetch(`${CLOUD_SYNC_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...(init.headers ?? {}),
    },
  });
}

/**
 * Verify the identity round-trip end-to-end. Called once on Range Log mount
 * after Sign-In so we can show the user a "Welcome back, Sal" toast.
 */
export async function verifyIdentity(): Promise<CloudSyncResult<{ customerId: string }>> {
  try {
    const res = await authedFetch('/v1/identity/verify', { method: 'POST' });
    if (res.status === 401) return { ok: false, error: 'unauthorized' };
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    const json = (await res.json()) as { customerId: string };
    return { ok: true, data: json };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/** Push a v3 session to cloud. */
export async function pushSession(session: ShotSession): Promise<CloudSyncResult> {
  try {
    const res = await authedFetch('/v1/session/upsert', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: session.id,
        payload: stripLocalOnly(session),
        loggedAt: session.loggedAt,
      }),
    });
    if (res.status === 401) return { ok: false, error: 'unauthorized' };
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/** Pull all sessions from cloud (newest first). */
export async function pullSessions(): Promise<CloudSyncResult<ShotSession[]>> {
  try {
    const res = await authedFetch('/v1/sessions', { method: 'GET' });
    if (res.status === 401) return { ok: false, error: 'unauthorized' };
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    const json = (await res.json()) as {
      ok: boolean;
      sessions: Array<{ sessionId: string; payload: ShotSession; updatedAt: number }>;
    };
    return { ok: true, data: json.sessions.map((s) => s.payload) };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/** Delete a session from cloud (best-effort; local delete still happens). */
export async function deleteCloudSession(sessionId: string): Promise<CloudSyncResult> {
  try {
    const res = await authedFetch(`/v1/session/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    });
    if (res.status === 401) return { ok: false, error: 'unauthorized' };
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/**
 * Strip local-only fields before cloud upload:
 *   - photoDataUri on each set (Pro-tier photo backup is a separate surface)
 *   - notes that look PII-ish? — NO. The user opted into sync; their notes
 *     come with. We do not silently censor user content.
 *
 * In v136.0 we strip ONLY the photo data URIs because they're large and
 * Pro-only. Everything else syncs as-is.
 */
function stripLocalOnly(session: ShotSession): ShotSession {
  const copy: ShotSession = { ...session };
  if (copy.sets) {
    copy.sets = copy.sets.map((set) => ({
      ...set,
      photoDataUri: undefined,
    }));
  }
  if (copy.richShots) {
    copy.richShots = copy.richShots.map((s) => ({
      ...s,
      photoDataUri: undefined,
    }));
  }
  return copy;
}

/**
 * Merge cloud sessions with local sessions, keeping newest by `loggedAt`.
 * Returns the deduped set. Used during sign-in backfill and pull-on-mount.
 */
export function mergeSessions(
  local: readonly ShotSession[],
  cloud: readonly ShotSession[],
): ShotSession[] {
  const byId = new Map<string, ShotSession>();
  for (const s of local) byId.set(s.id, s);
  for (const s of cloud) {
    const existing = byId.get(s.id);
    if (!existing) {
      byId.set(s.id, s);
    } else {
      // Prefer the newer loggedAt; ties favor cloud (canonical).
      const lDate = Date.parse(existing.loggedAt);
      const cDate = Date.parse(s.loggedAt);
      if (cDate >= lDate) byId.set(s.id, s);
    }
  }
  return Array.from(byId.values()).sort((a, b) => b.loggedAt.localeCompare(a.loggedAt));
}

// ════════════════════════════════════════════════════════════════════════════
// Gear sync (Layer 2) — bow + arrow profiles via /v1/gear
// ════════════════════════════════════════════════════════════════════════════
//
// The tool pages (My Bows / My Arrows) own native bow/arrow profiles. Layer 1
// made those durable per-device (parent-origin mirror); this makes them survive
// a new device too, through the same proven identity-authed worker the Range
// Log uses. One JSON blob per (customer, kind) in D1.

export type GearKind = 'bow_profiles' | 'arrow_profiles';

/** Pull one gear blob. ok:false when anonymous/offline; data null when unsynced. */
export async function fetchGear<T = unknown>(kind: GearKind): Promise<CloudSyncResult<T | null>> {
  try {
    const res = await authedFetch(`/v1/gear/${kind}`, { method: 'GET' });
    if (res.status === 401) return { ok: false, error: 'unauthorized' };
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    const json = (await res.json()) as { value?: T | null };
    return { ok: true, data: (json.value ?? null) as T | null };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/** Push one gear blob (whole snapshot). Best-effort; never throws. */
export async function pushGear(kind: GearKind, value: unknown): Promise<CloudSyncResult> {
  try {
    const res = await authedFetch(`/v1/gear/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
    if (res.status === 401) return { ok: false, error: 'unauthorized' };
    if (!res.ok) return { ok: false, error: `http_${res.status}` };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/** Fetch the customer's bow profiles from cloud, or null if unsynced/anonymous. */
export async function fetchRemoteBowProfiles(): Promise<BowProfile[] | null> {
  const r = await fetchGear<BowProfile[]>('bow_profiles');
  return r.ok && Array.isArray(r.data) ? r.data : null;
}

/** Fetch the customer's arrow profiles from cloud, or null if unsynced/anonymous. */
export async function fetchRemoteArrowProfiles(): Promise<ArrowProfile[] | null> {
  const r = await fetchGear<ArrowProfile[]>('arrow_profiles');
  return r.ok && Array.isArray(r.data) ? r.data : null;
}

export function pushRemoteBowProfiles(profiles: BowProfile[]): Promise<CloudSyncResult> {
  return pushGear('bow_profiles', profiles);
}

export function pushRemoteArrowProfiles(profiles: ArrowProfile[]): Promise<CloudSyncResult> {
  return pushGear('arrow_profiles', profiles);
}
